// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import color from "@hardwoods/XDL/Utilities/color";
import EventBus from "@hardwoods/XDL/Utilities/EventBus";
import hexToRGB from "@hardwoods/XDL/Utilities/hexToRgba";
import { Session } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { css } from "styled-components";

export const isMobileDevice = () => typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;

export const isTabletDevice = () => typeof window !== "undefined" && window.matchMedia("(max-width: 1024px)").matches;

export const capitalize = (text?: string) => {
    if (!text) {
        return text;
    }

    return text.toLowerCase().replace(/\b./g, (a: string) => {
        return a.toUpperCase();
    });
};

export const ellipsis = (maxNoLines: number) => {
    return css`
        overflow: hidden;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: ${maxNoLines};
    `;
};

const isEmpty = (value: string) => value.trim().length <= 0;

const isEventSupported = (eventName: string) => {
    let el: any = document.createElement("div");
    eventName = `on${eventName}`;
    let isSupported = eventName in el;
    if (!isSupported) {
        el.setAttribute(eventName, "return;");
        isSupported = typeof el[eventName] === "function";
    }
    el = null;
    return isSupported;
};

function pickMultiple<T extends {}, Exclude extends keyof T>(object: T, keys: Readonly<Exclude[]>): Pick<T, Exclude> {
    const result: any = { ...object };
    Object.keys(object).forEach((key: any) => {
        if (!keys.includes(key)) {
            delete result[key];
        }
    });

    return result;
}

export const pluralize = (count: number, noun: string, suffix = "s", withNumber = false) =>
    `${withNumber ? `${count} ` : ""}${noun}${count !== 1 ? suffix : ""}`;

export const isTextWrap = (element: HTMLElement) => {
    if (!element) {
        return false;
    }
    const item = element.cloneNode(true) as any;
    item.style.visibility = "hidden";
    document.body.appendChild(item);
    const clnHeight = item.clientHeight;
    item.parentNode.removeChild(item);
    return clnHeight < element.clientHeight;
};

export const isAuthenticated = (session: Session) =>
    session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;

export const hideScrollBar = css`
    &::-webkit-scrollbar {
        display: none;
    }
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
`;

export const openLiveChat = () => {
    if (typeof window !== "undefined" && (window as any).acquireIO) {
        (window as any).acquireIO.ui.status("max");
    }
};

// To load JS script with callback
// example:
// getScripts.urls([ // url1, url2]).then(() => {
// callback handler
// });
export const getScripts = {
    url: (url: string) => {
        return new Promise((resolve, reject) => {
            if (typeof document !== "undefined") {
                const script = document.createElement("script");
                script.type = "text/javascript";
                script.src = url;
                script.addEventListener("load", () => resolve(script), false);
                script.addEventListener("error", () => reject(script), false);
                document.body.appendChild(script);
            }
        });
    },
    urls: (urls: string[]) => {
        return Promise.all(urls.map(getScripts.url));
    },
};

export const xIsAuthenticated = (session: Session) =>
    session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;

export const xSiteMessage = (messageName: string, ...replacementValues: string[]) => {
    const message = siteMessage(messageName, ...replacementValues);
    if (typeof message === "string" && message === messageName) {
        return;
    }

    return message;
};

export const getFilterDate = (dateText: string) =>
    new Date(new Date(dateText).getFullYear(), new Date(dateText).getMonth(), new Date(dateText).getDate());

export const getFormattedDate = (date: Date) => {
    const year = date.getFullYear();

    let month = (1 + date.getMonth()).toString();
    month = month.length > 1 ? month : `0${month}`;

    let day = date.getDate().toString();
    day = day.length > 1 ? day : `0${day}`;

    return `${year}-${month}-${day}`;
};

export { hexToRGB, color, isEmpty, isEventSupported, pickMultiple, EventBus };
